// store/actions/imActions.js
import { IMSDK } from '@/common/imSdk/index';
import { LoginMsg } from '@/common/imSdk/protobuf/login';
import { PRO_IM_URL } from "@/common/Url"
import SessionCache from '@/common/Session'

const actions = {
    async initIM({ commit, dispatch, state }, { account, token, callbackv2 }) {
        if (state.isLogin && state.imInstans) {
            if (callbackv2) callbackv2();
            return;
        }

        if (state.imInstans) {
            try {
                state.imInstans.dispose();
            } catch (e) {
                console.log('清理旧IM实例失败:', e);
            }
        }

        const imInstance = new IMSDK(PRO_IM_URL, {
            onconnect: async () => {
                try {
                    await imInstance.login(token, account, LoginMsg.Role.WEB_CLIENT);
                    commit('SET_USER_GUID', account);
                    commit('SET_LOGIN', true);
                    commit('SET_IM_INSTANCE', imInstance);
                    await dispatch('resetAndLoadFriends');
                    if (callbackv2) callbackv2();
                    dispatch('startHeartbeat');
                } catch (error) {
                    console.error('IM登录失败:', error);
                    commit('SET_LOGIN', false);
                }
            },
            ondisconnect: () => {
                commit('SET_LOGIN', false);
            },
            onerror: (error) => {
                console.error('WebSocket错误:', error);
                commit('SET_LOGIN', false);
            },
            onmsg: (msg) => {
                if (msg && (msg.code === 200 || msg.code === undefined)) {
                    dispatch('handleRawMessage', msg, { root: true });
                }
            }
        });
    },

    startHeartbeat({ dispatch }) {
        if (this._heartbeatTimer) clearInterval(this._heartbeatTimer);
        this._heartbeatTimer = setInterval(() => dispatch('checkHeartbeat'), 30000);
    },

    async checkHeartbeat({ state, dispatch }) {
        if (!state.isLogin || !state.imInstans) return;
        const ws = state.imInstans.clientStater?.ws;
        if (!ws || ws.readyState !== WebSocket.OPEN) dispatch('handleDisconnect');
    },

    handleDisconnect({ commit, state, dispatch }) {
        const imInstance = state.imInstans;
        if (imInstance?.isConnected?.()) {
            imInstance.clientStater?.resetHeartbeatTimer();
            return;
        }

        commit('SET_LOGIN', false);
        const customerInfo = SessionCache.getCustomerInfo();
        const token = SessionCache.getAccessToken();
        if (customerInfo?.account?.guid && token) {
            dispatch('scheduleReconnect', { account: customerInfo.account.guid, token });
        }
    },

    scheduleReconnect({ dispatch }, { account, token, retryCount = 0 }) {
        if (retryCount > 5) return;
        const delay = Math.min(1000 * Math.pow(2, retryCount), 30000);
        setTimeout(() => {
            dispatch('initIM', {
                account, token,
                callbackv2: () => dispatch('resetAndLoadFriends')
            }).catch(() => {
                dispatch('scheduleReconnect', { account, token, retryCount: retryCount + 1 });
            });
        }, delay);
    },

    async updateSessionUnreadCount({ commit }, { sessionId, unreadCount }) {
        if (!sessionId) return false;
        commit('im/UPDATE_UNREAD_COUNT', { sessionId, unreadCount }, { root: true });
        commit('im/FORCE_REFRESH_CONVERSATIONS', null, { root: true });
        try {
            const EventBus = require('@/common/EventBus').EventBus;
            EventBus.$emit('unread-messages-updated', { timestamp: Date.now(), sessionId });
        } catch (e) {
            console.warn('[IM] 触发未读更新事件失败:', e);
        }
        return true;
    },

    // 获取历史消息 - 移除 pageSize 和 hasMore 参数
    async getHistory({ state }, { fromGuid, targetGuid, lastMsgGuid = '', timeout = 10000 }) {
        if (!state.imInstans || !fromGuid || !targetGuid) {
            return { messages: [] }
        }

        try {

            let res
            if (typeof state.imInstans.getHistoryMsg === 'function') {
                try {
                    // 尝试不同的参数顺序
                    res = await state.imInstans.getHistoryMsg(fromGuid, targetGuid, lastMsgGuid)
                } catch (e) {
                    try {
                        // 尝试不带 lastMsgGuid
                        res = await state.imInstans.getHistoryMsg(fromGuid, targetGuid)
                    } catch (e2) {
                        return { messages: [] }
                    }
                }
            } else {
                return { messages: [] }
            }

            if (res && res.code === 200) {
                let data = []
                // 解析不同格式的响应
                if (res.msg?.msg) {
                    data = Array.isArray(res.msg.msg) ? res.msg.msg : [res.msg.msg]
                } else if (res.data?.records) {
                    data = res.data.records
                } else if (Array.isArray(res.data)) {
                    data = res.data
                } else if (Array.isArray(res)) {
                    data = res
                }
                // 格式化消息
                const formattedMessages = data.map(msg => ({
                    guid: msg.guid || msg.id,
                    id: msg.guid || msg.id,
                    fromGuid: msg.fromGuid,
                    targetGuid: msg.targetGuid,
                    contentType: msg.contentType || 1,
                    data: msg.data || {},
                    time: msg.time || Date.now(),
                    readStatus: msg.readStatus || 0,
                    content: msg.content || ''
                }))

                return { messages: formattedMessages }
            }

            return { messages: [] }
        } catch (error) {
            console.error('[IM] getHistory 执行失败:', error)
            return { messages: [], error }
        }
    },

    // 获取全部历史消息（循环获取直到没有更多）
    async getAllHistory({ dispatch, state }, { fromGuid, targetGuid }) {
        if (!state.imInstans || !fromGuid || !targetGuid) {
            return { messages: [] }
        }

        let allMessages = []
        let lastMsgGuid = ''
        let loopCount = 0
        const maxLoops = 50 // 最多循环50次，防止无限循环

        while (loopCount < maxLoops) {
            loopCount++
            const result = await dispatch('getHistory', {
                fromGuid,
                targetGuid,
                lastMsgGuid
            })

            if (result && result.messages && result.messages.length > 0) {
                // 去重
                const existingGuids = new Set(allMessages.map(m => m.guid))
                const newMessages = result.messages.filter(m => !existingGuids.has(m.guid))

                if (newMessages.length > 0) {
                    allMessages = [...newMessages, ...allMessages] // 保持时间正序
                }

                // 更新 lastMsgGuid 为当前批次最早的消息（用于获取更早的历史）
                // 注意：result.messages 是按时间倒序排列的，最后一条是最早的消息
                if (result.messages.length > 0) {
                    const earliestMsgInPage = result.messages[result.messages.length - 1]
                    const newLastMsgGuid = earliestMsgInPage?.guid || ''

                    // 如果 lastMsgGuid 没有变化，说明已经到底了
                    if (newLastMsgGuid === lastMsgGuid) {
                        break
                    }
                    lastMsgGuid = newLastMsgGuid
                } else {
                    break
                }
            } else {
                // 没有返回消息，停止加载
                break
            }
        }

        // 按时间排序
        allMessages.sort((a, b) => (a.time || 0) - (b.time || 0))

        return { messages: allMessages }
    },

    async logout({ state }, { fromGuid }) {
        clearTimeout(state._st);
        if (state.imInstans) {
            try {
                await state.imInstans.loginout(fromGuid, LoginMsg.Role.WEB_CLIENT);
            } catch (error) {
                console.error('Logout error:', error);
            }
        }
    },

    async markMessagesAsRead({ commit, state }, { fromGuid, targetGuid }) {
        if (!state.imInstans) return false;
        try {
            if (typeof state.imInstans.clearUnread === 'function') {
                await state.imInstans.clearUnread(fromGuid, [targetGuid]);
            }
            commit('im/UPDATE_UNREAD_COUNT', { sessionId: targetGuid, unreadCount: 0 }, { root: true });
            commit('im/FORCE_REFRESH_CONVERSATIONS', null, { root: true });
            return true;
        } catch (error) {
            console.error('标记已读失败:', error);
            return false;
        }
    },

    async updateSessionMessages({ commit, state }, { sessionId, messages }) {
        if (!sessionId || !messages) return false;

        const sessionMap = state.sessionMap instanceof Map ? new Map(state.sessionMap) : new Map();
        let session = sessionMap.get(sessionId);

        if (!session) {
            session = {
                messages: [],
                unreadCount: 0,
                lastMsg: null,
                lastMsgTime: null,
                friendInfo: null
            };
            sessionMap.set(sessionId, session);
        }

        // 合并消息，去重
        const existingGuids = new Set(session.messages.map(m => m?.guid).filter(Boolean));
        const newMessages = messages.filter(m => m?.guid && !existingGuids.has(m.guid));

        if (newMessages.length > 0) {
            session.messages = [...session.messages, ...newMessages];
            session.messages.sort((a, b) => (a.time || 0) - (b.time || 0));

            if (session.messages.length) {
                session.lastMsg = session.messages[session.messages.length - 1];
                session.lastMsgTime = session.lastMsg.time;
            }

            commit('SET_SESSION_MAP', sessionMap);
        }

        return true;
    },
};

export default actions;